import type { CompanyFilterOptions } from '@/components/shared/LoadsTable/CompanyFilterButton'
import type { LocationData } from '@/features/load-hub/types/filters'
import { locationChipLabel } from '@/features/load-hub/utils/locationSelection'
import type { BoxTruckLoadType } from '../hooks/requestMapping'
import type { BoxTruckRefinements } from '../components/BoxTruckRefinementBar'
import { countActiveAdvanced, defaultRefinementsForBoard } from './boxTruckAdvancedFilterState'
import { BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS, type BoxTruckCapacityLimits, boxTruckDatSelection, isDefaultBoxTruckCapacityLimits } from '@numeo/types-core'

interface LaneSecondaryFilters {
    companyFilter: CompanyFilterOptions
    defaultCityRadiusMi: number
    destinationRadius: number | null
    fullPartial: BoxTruckLoadType
    hiddenSourceCount: number
    lengthFt: number | null
    /** The request's own freshness bound, so a changed Max Age reads as an active filter here too. */
    maxPostedAgeHours: number
    originRadius: number | null
    weightLbs: number | null
    capacityLimits: BoxTruckCapacityLimits
    datEnabled: boolean
    datEquipmentTypes: string[]
    equipmentLabels: string[]
}

interface BoxTruckSecondaryFilters {
    lane?: LaneSecondaryFilters
    query: string
    refinements: BoxTruckRefinements
    selectedDriverCount: number
}

interface ResettableSecondaryFilters {
    refinements: BoxTruckRefinements
    defaultRefinements: BoxTruckRefinements
    /** A pinned bids board is on screen. Both of them (Driver Bids, My Biddings) run no lane query, so none
     *  of the lane terms below exist to reset there. */
    pinnedBoardActive: boolean
    lane: {
        fullPartial: BoxTruckLoadType
        lengthFt: number | null
        /** The request's own freshness bound, so a changed Max Age badges the compact board too. */
        maxPostedAgeHours: number
        weightLbs: number | null
        capacityLimits: BoxTruckCapacityLimits
        includeUnknownCapacity: boolean
        datEquipmentTypes: string[]
        equipmentLabels: string[]
    }
}

/**
 * Whether Reset has anything to undo — what decides if the button is shown at all.
 *
 * Every term compares against its DEFAULT rather than testing truthiness, and that distinction is the whole
 * point: two of these now START set — unknown capacity passes, and every DAT subtype is ticked. A truthiness
 * test leaves Reset permanently visible on a board nobody has touched, so there is no clean state to return to.
 *
 * Scoped to FILTERS. The DAT source switch is deliberately absent: Reset must not silently take a feed away.
 */
export const hasResettableBoxTruckSecondaryFilters = ({ refinements, defaultRefinements, pinnedBoardActive, lane }: ResettableSecondaryFilters): boolean => {
    if ((Object.keys(refinements) as (keyof BoxTruckRefinements)[]).some((key) => refinements[key] !== defaultRefinements[key])) return true
    if (pinnedBoardActive) return false
    return (
        lane.fullPartial !== 'BOTH' ||
        lane.lengthFt != null ||
        lane.weightLbs != null ||
        // Reset clears Max Age back to the default, so it has to count here as well — otherwise an operator who
        // only widened the age has a filter the board applies and no button offering to undo it.
        lane.maxPostedAgeHours !== BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS ||
        !isDefaultBoxTruckCapacityLimits(lane.capacityLimits) ||
        !lane.includeUnknownCapacity ||
        // The DAT switch is a SOURCE, not a filter. Reset used to turn it off, which cost the operator the
        // feed for a day (it is remembered) for pressing a button that says it clears filters.
        boxTruckDatSelection(lane.equipmentLabels, lane.datEquipmentTypes).narrowed
    )
}

export const isBoxTruckCompanyFilterActive = (filter: CompanyFilterOptions): boolean =>
    filter.listType !== 'all' || filter.mode !== 'show' || filter.companyNames.length > 0

/** Compact location label that still counts driver GPS pins without a reverse-geocoded city/state. */
export const summarizeBoxTruckLocations = (locations: LocationData[], fallback: string, visibleCount = 2): string => {
    const summaries = locations.map((location) => ({ label: locationChipLabel(location), pinned: location.latitude != null }))
    const labels = summaries.map(({ label }) => label).filter(Boolean)
    const pinnedCount = summaries.filter(({ label, pinned }) => !label && pinned).length
    if (labels.length === 0) {
        if (pinnedCount === 0) return fallback
        return pinnedCount === 1 ? 'Pinned location' : `${pinnedCount} pinned locations`
    }

    const visibleLabels = labels.slice(0, visibleCount)
    const omittedCount = labels.length - visibleLabels.length + pinnedCount
    return `${visibleLabels.join(', ')}${omittedCount > 0 ? ` +${omittedCount}` : ''}`
}

const isCustomizedRadius = (radius: number | null, defaultCityRadiusMi: number): boolean => radius != null && radius !== defaultCityRadiusMi

/** Whether filters omitted from the compact lane summary are still narrowing the board. */
export const hasActiveBoxTruckSecondaryFilters = ({ lane, query, refinements, selectedDriverCount }: BoxTruckSecondaryFilters): boolean => {
    if (query.trim() || refinements.minMi !== '' || refinements.maxMi !== '') return true
    // On Driver Bids there is no lane summary, so its driver selection remains an additional filter. On a
    // search tab the picked drivers have already populated the summarized origin/equipment and are not extra.
    // No lane means Driver Bids, which has no search filters of its own.
    if (!lane)
        return (
            selectedDriverCount > 0 ||
            countActiveAdvanced(
                { fullPartial: 'BOTH', lengthFt: null, weightLbs: null, maxPostedAgeHours: BOX_TRUCK_DEFAULT_MAX_POSTED_AGE_HOURS },
                refinements,
                defaultRefinementsForBoard()
            ) > 0
        )

    return (
        countActiveAdvanced(
            { fullPartial: lane.fullPartial, lengthFt: lane.lengthFt, weightLbs: lane.weightLbs, maxPostedAgeHours: lane.maxPostedAgeHours },
            refinements,
            defaultRefinementsForBoard()
        ) > 0 ||
        isCustomizedRadius(lane.originRadius, lane.defaultCityRadiusMi) ||
        isCustomizedRadius(lane.destinationRadius, lane.defaultCityRadiusMi) ||
        lane.hiddenSourceCount > 0 ||
        !isDefaultBoxTruckCapacityLimits(lane.capacityLimits) ||
        // Same scoped reading the request builder uses, so the badge cannot claim a filter the search does
        // not apply (or stay silent on one it does).
        (lane.datEnabled && boxTruckDatSelection(lane.equipmentLabels, lane.datEquipmentTypes).narrowed) ||
        isBoxTruckCompanyFilterActive(lane.companyFilter)
    )
}
